import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance } from 'vue';
import ReportTestingMethodsFilters from '@/_core/entities/report/ui/ReportTestingMethodsFilters/ReportTestingMethodsFilters.vue';
import ComplianceFilterDropdown from '@/_core/widgets/ComplianceFilterDropdown/ComplianceFilterDropdown.vue';
import type { TComplianceSummary } from '@/_core/entities/report/models/types';
import ReportVulnerabilityListItem from '@/_core/entities/report/ui/ReportVulnerabilityListItem/ReportVulnerabilityListItem.vue';
import ReportVulnerabilityListItemSkeleton from '@/_core/entities/report/ui/ReportVulnerabilityListItem/ReportVulnerabilityListItemSkeleton.vue';
import ReportPagination from '@/_core/entities/report/ui/ReportPagination/ReportPagination.vue';
import type {
  TFolder,
  TReportPageResponse,
  TTestingMethod,
} from '@/_core/entities/report/models/types';
import type { TVulnerability } from '@/_core/entities/vulnerability/models/types';
import {
  parseTestingMethod,
  useActiveFolderName,
} from '@/_core/entities/report/utils/report.utils';
import {
  DEFAULT_FOLDER_NAMES,
  DEFAULT_TESTING_METHOD,
  REPORT_PAGE_LIMIT,
  REPORT_ROUTE_NAMES,
} from '@/_core/entities/report/models/consts';
import { getQueryParamValue } from '@/_core/shared/utils/getQueryParamValue';
import { RawLocation } from 'vue-router';
import { useMoveVulnerabilityToFolder } from '@/_core/entities/vulnerability/api/useCreateFolderAndMove';
import { useFalsePositiveFolder } from '@/_core/entities/vulnerability/utils/useFalsePositiveFolder';
import { buildMoveFolderPayload } from '@/_core/entities/vulnerability/utils/buildMovePayload';
import { useToast } from '@/_core/shared/ui/AppToast/useToast';


export default /*#__PURE__*/_defineComponent({
  __name: 'ReportVulnerabilitiesList',
  props: {
    setPage: null,
    currentPage: null,
    totalPages: null,
    foldersData: null,
    pageData: null,
    isDataLoading: { type: Boolean },
    isFolderEmpty: { type: Boolean },
    complianceSummary: null,
    selectedRequirementId: null,
    selectedItemId: null
  },
  emits: ["complianceSelect", "complianceClear"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'complianceSelect', payload: { requirementId?: string; itemId?: string }): void;
  (e: 'complianceClear'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  setPage(page: number): void;
  currentPage: number;
  totalPages: number;
  foldersData?: TFolder[];
  pageData?: TReportPageResponse;
  isDataLoading: boolean;
  isFolderEmpty?: boolean;
  complianceSummary?: TComplianceSummary;
  selectedRequirementId?: string;
  selectedItemId?: string;
};





const instance = getCurrentInstance();
const route = computed(() => instance?.proxy.$route);

const activeFolderName = useActiveFolderName(route);

const activeFolder = computed(() =>
  props.foldersData?.find((f) => f.name === activeFolderName.value)
);

const isInFalsePositivesFolder = computed(
  () => activeFolderName.value === DEFAULT_FOLDER_NAMES.falsePositives
);

const testingMethodCounts = computed(
  () => activeFolder.value?.testingMethodCounts ?? { all: 0, sast: 0, dast: 0, sastDast: 0 }
);

const activeTestingMethod = computed(() => {
  return parseTestingMethod(
    getQueryParamValue(route.value?.query?.testingMethod, DEFAULT_TESTING_METHOD)
  );
});

const skeletonCount = computed(() => {
  const method = activeTestingMethod.value;
  const countKey = method === 'sast-dast' ? 'sastDast' : method;
  const count = testingMethodCounts.value[countKey];
  return count > 0 && count < REPORT_PAGE_LIMIT ? count : REPORT_PAGE_LIMIT;
});

const toast = useToast();
const { mutateAsync: moveVulnerability } = useMoveVulnerabilityToFolder();
const falsePositiveFolder = useFalsePositiveFolder(computed(() => props.foldersData ?? []));

const mainReportFolder = computed(
  () => props.foldersData?.find((f) => f.name === DEFAULT_FOLDER_NAMES.mainReport) ?? null
);

async function moveVulnerabilityToTarget(
  vulnerability: TVulnerability,
  folderName: string,
  holderId: string
) {
  const params = route.value?.params;
  const payload = params
    ? buildMoveFolderPayload(params, vulnerability.uid, holderId, folderName)
    : null;

  if (!payload) return;

  try {
    await moveVulnerability(payload);

    toast.success({ message: `Vulnerability moved to "${folderName}".` });
  } catch {
    toast.error({ message: 'Failed to move vulnerability' });
  }
}

function handleFalsePositive(vulnerability: TVulnerability) {
  const folder = falsePositiveFolder.value;
  if (!folder) return;
  moveVulnerabilityToTarget(vulnerability, folder.name, folder.holderId);
}

function handleRemoveFromFalsePositives(vulnerability: TVulnerability) {
  const folder = mainReportFolder.value;
  if (!folder) return;
  moveVulnerabilityToTarget(vulnerability, folder.name, folder.holderId);
}

function getVulnerabilityTo(uid: string): RawLocation {
  const params = route.value?.params;

  if (params?.jobId) {
    return {
      name: REPORT_ROUTE_NAMES.singleScanVulnerability,
      params: { jobId: params.jobId, uid },
    };
  }

  return {
    name: REPORT_ROUTE_NAMES.versionVulnerability,
    params: {
      integrationId: params?.integrationId ?? '',
      branchName: params?.branchName ?? '',
      versionId: params?.versionId ?? '',
      uid,
    },
  };
}

function handleTestingMethodFilterChange(method: TTestingMethod) {
  instance?.proxy.$router.push({
    query: {
      ...instance.proxy.$route.query,
      testingMethod: method,
    },
  });

  if (props.currentPage === 1) return;

  props.setPage(1);
}

return { __sfc: true,props, emit, instance, route, activeFolderName, activeFolder, isInFalsePositivesFolder, testingMethodCounts, activeTestingMethod, skeletonCount, toast, moveVulnerability, falsePositiveFolder, mainReportFolder, moveVulnerabilityToTarget, handleFalsePositive, handleRemoveFromFalsePositives, getVulnerabilityTo, handleTestingMethodFilterChange, ReportTestingMethodsFilters, ComplianceFilterDropdown, ReportVulnerabilityListItem, ReportVulnerabilityListItemSkeleton, ReportPagination }
}

})